import { useCallback, useRef, useState } from 'react'
import {
  Typography,
  FormControl,
  styled,
  IconButton,
  Checkbox,
  Stack,
} from '@mui/material'
import { PrimaryButton } from '$common/buttons'
import { AuthContainer } from '$common/auth-container'
import { ArrowRightIcon } from '$common/icons'
import { PhoneInput, PhoneInputRef } from '$common/input'
import { signInUser } from '$logic/auth'
import { goToAuthStartingPage, goToAuthVerify } from '$logic/router'
import { isPhoneValidByLib } from '$func/phone'
import { AuthHeader } from '$common/auth-header/auth-header'
// Localization
import { t } from '$i18n'
import { isContactPermissionAllowedByClientUser } from '$logic/twaCommunication'
import { changeAutoSyncContacts } from '$logic/contacts/service'
import { showSwitchAutoSyncActivationSnackbar } from '$comp/snackbars'

const Fields = styled('div')<{ hasError?: boolean }>`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  margin-bottom: 17px;
`

export function SignIn() {
  const phoneInputRef = useRef<PhoneInputRef>(null)

  // TODO: after new design values like phone can move to phone-input component and be received via useImperativeHandle
  const [phone, setPhone] = useState('')
  const [alertText, setAlertText] = useState('')
  const [syncContactsActivated, setSyncContactsActivated] = useState(false)
  const toggleContactActivation = () => {
    setSyncContactsActivated(prevSyncContactsActivated => {
      analite('SIGN_IN_SYNC_CONTACTS', {
        active: !prevSyncContactsActivated,
      })
      void changeAutoSyncContacts(!prevSyncContactsActivated)
      showSwitchAutoSyncActivationSnackbar(!prevSyncContactsActivated)
      return !prevSyncContactsActivated
    })
  }
  const isSubmittedRef = useRef<boolean>(false)
  const isContactPermissionAllowed = isContactPermissionAllowedByClientUser()
  const onSubmit = useCallback(async () => {
    if (isSubmittedRef.current) return
    isSubmittedRef.current = true
    if (!phone) {
      setAlertText(t('phoneInput.errorMessage'))
      return
    }

    const fullNumber = phoneInputRef.current?.getPhone(phone)
    if (!fullNumber) return
    const isValid = isPhoneValidByLib(fullNumber)
    if (isValid) {
      const verificationSession = await signInUser(fullNumber)
      if (verificationSession) goToAuthVerify(verificationSession)
    } else setAlertText(t('signin.serverCodes.INVALID_PHONE'))
    isSubmittedRef.current = false
  }, [phone])

  const onPhoneChange = useCallback(
    (phoneNumber: string) => {
      if (isSubmittedRef.current) isSubmittedRef.current = false
      if (alertText) {
        setAlertText('')
      }
      setPhone(phoneNumber)
    },
    [alertText],
  )

  return (
    <AuthContainer>
      <AuthHeader>
        <IconButton
          edge="end"
          sx={{ color: 'neutral500' }}
          onClick={goToAuthStartingPage}
        >
          <ArrowRightIcon />
        </IconButton>
        <Typography variant="h3">{t('signin.title')}</Typography>
      </AuthHeader>
      <Fields hasError={!!alertText}>
        <Typography variant="body2" mb="16px">
          {t('signin.enterPhone')}
        </Typography>
        {/* TODO logic for hasError prop  */}
        <FormControl
          sx={{ width: '100%' }}
          onSubmit={onSubmit}
          onKeyDown={e => {
            if (e.key === 'Enter') onSubmit()
          }}
        >
          <PhoneInput
            value={phone}
            onChange={onPhoneChange}
            hasError={!!alertText}
            errorMessage={alertText}
            ref={phoneInputRef}
          />
        </FormControl>
        {isContactPermissionAllowed && (
          <Stack direction="row" gap="6px">
            <Checkbox
              size="small"
              checked={syncContactsActivated}
              onChange={toggleContactActivation}
              sx={{
                color: 'neutral300',
                // '.MuiCheckbox-root': {
                padding: '2px',
                // },
              }}
            />
            <Typography variant="body2">
              {t('signin.checkboxOption')}
            </Typography>
          </Stack>
        )}
      </Fields>
      <PrimaryButton
        type="submit"
        text={t('signin.confirm')}
        onClick={onSubmit}
      />
    </AuthContainer>
  )
}
